/**
 * 底部抽屉（bottom sheet）。手机上的成员、聊天、文件都装在这里。
 *
 * 为什么不引 UI 库：这个项目一直零 UI 依赖，为一个抽屉拉进几百 KB 不值；
 * 它要做的事只有三件 —— 从底部升起来、点遮罩或按返回键关掉、内容区可滚。
 *
 * 返回键：手机上抽屉开着按返回，用户的预期是「关抽屉」，不是「退出教室」。
 * 打开时往 history 压一层，popstate 就关；点关闭按钮时把那一层弹掉，
 * 否则下一次按返回会多退一步。
 */

import { useEffect, useRef, type ReactNode } from 'react'

interface Props {
  open: boolean
  title: string
  onClose: () => void
  children: ReactNode
  /** 标题右侧的附加内容（比如切换页签） */
  aside?: ReactNode
}

const HISTORY_KEY = 'whatylive.sheet'

export function BottomSheet({ open, title, onClose, children, aside }: Props) {
  // 记住是不是我们自己压的那一层，关闭时才 back；
  // 用户用返回键关的话那一层已经被浏览器弹掉了，再 back 会多退一步。
  const pushedRef = useRef(false)
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  useEffect(() => {
    if (!open) return
    try {
      history.pushState({ [HISTORY_KEY]: true }, '')
      pushedRef.current = true
    } catch {
      pushedRef.current = false
    }
    const onPop = () => {
      pushedRef.current = false
      onCloseRef.current()
    }
    window.addEventListener('popstate', onPop)
    return () => {
      window.removeEventListener('popstate', onPop)
      if (pushedRef.current) {
        pushedRef.current = false
        // 关闭时把自己压的那一层弹掉。这会再触发一次 popstate，
        // 但监听已经卸了，不会再调 onClose。
        try { history.back() } catch { /* 忽略 */ }
      }
    }
  }, [open])

  // 抽屉开着时锁住页面滚动，不然在抽屉里划到底会把整个教室带着滚。
  useEffect(() => {
    if (!open) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prev }
  }, [open])

  if (!open) return null
  return (
    <div className="sheet-mask" onClick={onClose} role="presentation">
      <div
        className="sheet"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sheet-grip" aria-hidden />
        <div className="sheet-head">
          <strong>{title}</strong>
          {aside}
          <button className="sheet-close" onClick={onClose} aria-label="关闭">×</button>
        </div>
        <div className="sheet-body">{children}</div>
      </div>
    </div>
  )
}
